import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';
import { updateAddToCartPriceLabels } from '@theme/add-to-cart-price';

/**
 * Quantity calculator component for star-cross tile patterns.
 * Handles separate calculations for first and second product boxes.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} firstSqFtInput
 * @property {HTMLInputElement} firstUnitsInput
 * @property {HTMLInputElement} secondSqFtInput
 * @property {HTMLInputElement} secondUnitsInput
 * @property {HTMLInputElement} perUnitCheckbox
 * @property {HTMLElement} firstPerUnitInputs
 * @property {HTMLElement} secondPerUnitInputs
 * @property {HTMLElement} firstSqFtWrapper
 * @property {HTMLElement} secondSqFtWrapper
 * @property {HTMLElement} firstBoxesDisplay
 * @property {HTMLElement} secondBoxesDisplay
 * @property {HTMLElement} completeCoverageDisplay
 * @property {HTMLElement} firstCoverageDisplay
 * @property {HTMLElement} secondCoverageDisplay
 * @property {HTMLElement} [firstBoxesLabel]
 * @property {HTMLElement} [secondBoxesLabel]
 * @property {HTMLElement} [firstCoverageLabel]
 * @property {HTMLElement} [secondCoverageLabel]
 * @property {HTMLElement} [firstUnitsLabel]
 * @property {HTMLElement} [secondUnitsLabel]
 * @property {HTMLElement} [overageDropdown]
 * @property {HTMLButtonElement} [dropdownTrigger]
 * @property {HTMLElement} [dropdownMenu]
 * @property {HTMLElement} [dropdownValue]
 * @property {HTMLElement} [overageDisplay]
 * @property {HTMLInputElement} [firstVariantIdInput]
 * @property {HTMLInputElement} [firstQuantityInput]
 * @property {HTMLInputElement} [secondVariantIdInput]
 * @property {HTMLInputElement} [secondQuantityInput]
 *
 * @extends {Component<Refs>}
 */
class QuantityCalculatorStarCross extends Component {
  /**
   * Coverage value per first product box (from product metafields).
   * @type {number}
   */
  firstCovers = 0;

  /**
   * Coverage value per second product box (from product metafields).
   * @type {number}
   */
  secondCovers = 0;

  /**
   * Overage percentage (default 0%).
   * @type {number}
   */
  overage = 0;

  /**
   * Selling unit name (singular).
   * @type {string}
   */
  sellingUnit = '';

  /**
   * Flag to prevent recursive updates between inputs.
   * @type {boolean}
   */
  #isUpdating = false;

  /**
   * Store original sq ft values (without overage) for recalculation.
   * @type {{first: number, second: number}}
   */
  #originalSqFt = { first: 0, second: 0 };


  connectedCallback() {
    super.connectedCallback();
    
    this.addEventListener('scroller-initialized', (/** @type {CustomEvent} */ e) => {
      const { scrollerType, label } = e.detail;
      this.#updatePlaceholders(scrollerType, label);
    });

    document.addEventListener('product-option-changed', this.#handleProductOptionChanged);
    document.addEventListener('change', this.#handleOptionCheckboxChange);
    
    requestIdleCallback(() => {
      this.#init();
      this.#setupFormHandler();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('product-option-changed', this.#handleProductOptionChanged);
    document.removeEventListener('change', this.#handleOptionCheckboxChange);
  }

  /**
   * Update input placeholders based on product label
   * @param {'first' | 'second'} scrollerType
   * @param {string} label
   */
  #updatePlaceholders(scrollerType, label) {
    const sqFtInput = scrollerType === 'first' ? this.refs.firstSqFtInput : this.refs.secondSqFtInput;
    const unitsInput = scrollerType === 'first' ? this.refs.firstUnitsInput : this.refs.secondUnitsInput;
    const unitsLabel = scrollerType === 'first' ? this.refs.firstUnitsLabel : this.refs.secondUnitsLabel;
    
    if (sqFtInput) {
      sqFtInput.placeholder = `${label} Square Feet`;
    }
    
    if (unitsInput) {
      const unit = this.sellingUnit === 'boxes' ? 'Boxes' : this.sellingUnit === 'sheets' ? 'Sheets' : 'Units';
      unitsInput.placeholder = `${label} ${unit}`;
    }

    if (scrollerType === 'first') {
      if (this.refs.firstBoxesLabel) {
        this.refs.firstBoxesLabel.textContent = `# of Boxes ${label}`;
      }
      if (this.refs.firstCoverageLabel) {
        this.refs.firstCoverageLabel.textContent = `${label} Only Coverage*`;
      }
    } else {
      if (this.refs.secondBoxesLabel) {
        this.refs.secondBoxesLabel.textContent = `# of Boxes ${label}`;
      }
      if (this.refs.secondCoverageLabel) {
        this.refs.secondCoverageLabel.textContent = `${label} Only Coverage*`;
      }
    }
  }

  /**
   * Setup form submit handler to remove empty items
   */
  #setupFormHandler() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    form.addEventListener('submit', () => {
      const itemsToRemove = [];
      
      for (let i = 0; i < 2; i++) {
        const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector(`input[name="items[${i}][id]"]`));
        const qtyInput = /** @type {HTMLInputElement | null} */ (form.querySelector(`input[name="items[${i}][quantity]"]`));
        
        const id = idInput?.value?.trim() || '';
        const qty = parseInt(qtyInput?.value || '0', 10);
        
        if (!id || qty === 0) {
          itemsToRemove.push(i);
        }
      }
      
      itemsToRemove.forEach(index => {
        const idInput = form.querySelector(`input[name="items[${index}][id]"]`);
        const qtyInput = form.querySelector(`input[name="items[${index}][quantity]"]`);
        
        if (idInput) idInput.remove();
        if (qtyInput) qtyInput.remove();
      });
    }, { capture: true });
  }

  /**
   * Initialize the calculator after refs are ready
   */
  #init() {
    if (!this.refs.firstSqFtInput || !this.refs.secondSqFtInput) {
      return;
    }
    
    const firstSqFtInput = /** @type {HTMLInputElement} */ (this.refs.firstSqFtInput);
    const secondSqFtInput = /** @type {HTMLInputElement} */ (this.refs.secondSqFtInput);
    const firstUnitsInput = /** @type {HTMLInputElement} */ (this.refs.firstUnitsInput);
    
    this.firstCovers = parseFloat(firstSqFtInput?.dataset?.covers || '0');
    this.secondCovers = parseFloat(secondSqFtInput?.dataset?.covers || '0');
    const placeholder = firstUnitsInput?.placeholder?.toLowerCase() || 'boxes (first)';
    this.sellingUnit = placeholder.includes('boxes') ? 'boxes' : 'units';
    
    firstSqFtInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e);
      this.handleFirstSqFtChange(e);
    });
    
    firstUnitsInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e, false);
      this.handleFirstUnitsChange(e);
    });
    
    secondSqFtInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e);
      this.handleSecondSqFtChange(e);
    });
    
    this.refs.secondUnitsInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e, false);
      this.handleSecondUnitsChange(e);
    });
    
    if (this.refs.perUnitCheckbox) {
      this.refs.perUnitCheckbox.addEventListener('change', (e) => {
        const target = /** @type {HTMLInputElement} */ (e.target);
        this.#togglePerUnitInputs(target.checked);
      });
    }
    
    this.#setupDropdown();
    this.clearCalculations();
    this.#setSecondInputsEnabled(false);
    this.#syncSecondOptionState();
    setTimeout(() => this.#syncSecondOptionState(), 0);
    setTimeout(() => this.#syncSecondOptionState(), 250);
  }

  /**
   * Handle product option change events.
   * @param {CustomEvent} event
   */
  #handleProductOptionChanged = (event) => {
    const { scrollerType } = event.detail || {};
    if (scrollerType !== 'second') return;

    this.#syncSecondOptionState();
  };

  /**
   * Handle checkbox changes for second option.
   * @param {Event} event
   */
  #handleOptionCheckboxChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!(target instanceof HTMLInputElement)) return;
    if (target.getAttribute('ref') !== 'showMainProductCheckbox') return;

    const scroller = target.closest('product-options-scroller-star-cross');
    if (!scroller) return;

    const scrollers = Array.from(document.querySelectorAll('product-options-scroller-star-cross'));
    if (scrollers[1] !== scroller) return;

    this.#syncSecondOptionState();
  };

  /**
   * Sync the enabled/disabled state for the second option inputs.
   */
  #syncSecondOptionState() {
    const scrollers = Array.from(document.querySelectorAll('product-options-scroller-star-cross'));
    const secondScroller = scrollers[1];
    if (!secondScroller) return;

    const hasSelection = Boolean(secondScroller.querySelector('button.active'));
    const checkbox = /** @type {HTMLInputElement | null} */ (secondScroller.querySelector('[ref="showMainProductCheckbox"]'));
    const shouldEnable = hasSelection && !checkbox?.checked;

    this.#setSecondInputsEnabled(shouldEnable);
  }

  /**
   * Enable/disable second option inputs.
   * @param {boolean} enabled
   */
  #setSecondInputsEnabled(enabled) {
    const secondSqFtInput = /** @type {HTMLInputElement | null} */ (this.refs.secondSqFtInput);
    const secondUnitsInput = /** @type {HTMLInputElement | null} */ (this.refs.secondUnitsInput);
    const secondSqFtWrapper = this.refs.secondSqFtWrapper;
    const secondPerUnitInputs = this.refs.secondPerUnitInputs;

    const applyStateToInput = (/** @type {HTMLInputElement | null} */ input) => {
      if (!input) return;
      input.disabled = !enabled;
      input.style.opacity = enabled ? '1' : '0.5';
      input.style.pointerEvents = enabled ? 'auto' : 'none';
      if (!enabled) {
        input.value = '';
        input.removeAttribute('value');
      }
    };

    const applyStateToContainer = (/** @type {HTMLElement | null} */ container) => {
      if (!container) return;
      container.style.opacity = enabled ? '1' : '0.5';
      container.style.pointerEvents = enabled ? 'auto' : 'none';
      const inputs = container.querySelectorAll('input');
      inputs.forEach((/** @type {HTMLInputElement} */ input) => {
        input.disabled = !enabled;
        if (!enabled) {
          input.value = '';
          input.removeAttribute('value');
        }
      });
    };

    applyStateToInput(secondSqFtInput);
    applyStateToInput(secondUnitsInput);
    applyStateToContainer(secondSqFtWrapper);
    applyStateToContainer(secondPerUnitInputs);

    if (!enabled) {
      this.#originalSqFt.second = 0;
      if (this.refs.secondQuantityInput) {
        this.refs.secondQuantityInput.value = '0';
      }
      this.updateDisplays();
    }
  }

  /**
   * Toggle per-unit inputs visibility for both first and second groups
   * @param {boolean} show
   */
  #togglePerUnitInputs(show) {
    const firstSqFtWrapper = this.refs.firstSqFtWrapper;
    const secondSqFtWrapper = this.refs.secondSqFtWrapper;
    const firstContainer = this.refs.firstPerUnitInputs;
    const secondContainer = this.refs.secondPerUnitInputs;
    
    if (show) {
      if (firstSqFtWrapper) firstSqFtWrapper.style.display = 'none';
      if (secondSqFtWrapper) secondSqFtWrapper.style.display = 'none';
      if (firstContainer) firstContainer.style.display = 'block';
      if (secondContainer) secondContainer.style.display = 'block';
    } else {
      if (firstSqFtWrapper) firstSqFtWrapper.style.display = 'block';
      if (secondSqFtWrapper) secondSqFtWrapper.style.display = 'block';
      if (firstContainer) firstContainer.style.display = 'none';
      if (secondContainer) secondContainer.style.display = 'none';
      
      if (this.refs.firstUnitsInput) this.refs.firstUnitsInput.value = '';
      if (this.refs.secondUnitsInput) this.refs.secondUnitsInput.value = '';
      
      if (this.refs.firstSqFtInput?.value) {
        const mockEvent = { target: this.refs.firstSqFtInput };
        this.handleFirstSqFtChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
      }
      if (this.refs.secondSqFtInput?.value) {
        const mockEvent = { target: this.refs.secondSqFtInput };
        this.handleSecondSqFtChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
      }
    }
  }

  /**
   * Setup dropdown functionality
   */
  #setupDropdown() {
    const { dropdownTrigger, dropdownMenu, overageDropdown } = this.refs;
    
    if (!dropdownTrigger || !dropdownMenu) return;
    
    dropdownTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggleDropdown();
    });
    
    const items = dropdownMenu.querySelectorAll('.quantity-calculator__dropdown-item');
    items.forEach(item => {
      item.addEventListener('click', (e) => {
        const button = /** @type {HTMLButtonElement} */ (e.currentTarget);
        const value = button.dataset.value || '0';
        const text = button.textContent || 'No overage';
        
        this.#selectOption(value, text);
        this.#closeDropdown();
      });
    });
    
    document.addEventListener('click', (e) => {
      if (overageDropdown && !overageDropdown.contains(/** @type {Node} */ (e.target))) {
        this.#closeDropdown();
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeDropdown();
      }
    });
  }

  /**
   * Toggle dropdown open/close
   */
  #toggleDropdown() {
    const { dropdownTrigger, dropdownMenu } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;
    
    const isExpanded = dropdownTrigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  }

  /**
   * Open dropdown
   */
  #openDropdown() {
    const { dropdownTrigger, dropdownMenu } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;
    
    dropdownTrigger.setAttribute('aria-expanded', 'true');
    dropdownMenu.setAttribute('aria-hidden', 'false');
  }

  /**
   * Close dropdown
   */
  #closeDropdown() {
    const { dropdownTrigger, dropdownMenu } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;
    
    dropdownTrigger.setAttribute('aria-expanded', 'false');
    dropdownMenu.setAttribute('aria-hidden', 'true');
  }

  /**
   * Select dropdown option
   * @param {string} value
   * @param {string} text
   */
  #selectOption(value, text) {
    const { dropdownValue, dropdownMenu } = this.refs;
    
    if (dropdownValue) {
      dropdownValue.textContent = text;
    }
    
    if (dropdownMenu) {
      const items = dropdownMenu.querySelectorAll('.quantity-calculator__dropdown-item');
      items.forEach(item => {
        const button = /** @type {HTMLButtonElement} */ (item);
        if (button.dataset.value === value) {
          button.setAttribute('aria-selected', 'true');
        } else {
          button.removeAttribute('aria-selected');
        }
      });
    }
    
    this.setOverage(parseFloat(value));
  }

  /**
   * Update unit label text based on quantity
   * @param {'first' | 'second'} scrollerType
   * @param {number} quantity
   */
  #updateUnitLabel(scrollerType, quantity) {
    const label = scrollerType === 'first' ? this.refs.firstUnitsLabel : this.refs.secondUnitsLabel;
    if (!label) return;
    
    let unitText = '';
    if (this.sellingUnit === 'boxes') {
      unitText = quantity === 1 ? 'box' : 'boxes';
    } else if (this.sellingUnit === 'sheets') {
      unitText = quantity === 1 ? 'sheet' : 'sheets';
    } else {
      unitText = quantity === 1 ? 'unit' : 'units';
    }
    
    label.textContent = unitText;
  }

  /**
   * Get plural or singular form of selling unit
   * @param {number} count
   * @returns {string}
   */
  #getUnitText(count) {
    if (count === 1) {
      if (this.sellingUnit.endsWith('xes')) {
        return this.sellingUnit.slice(0, -2);
      } else if (this.sellingUnit.endsWith('s')) {
        return this.sellingUnit.slice(0, -1);
      }
      return this.sellingUnit;
    } else {
      if (this.sellingUnit.endsWith('x')) {
        return this.sellingUnit + 'es';
      } else if (this.sellingUnit.endsWith('s')) {
        return this.sellingUnit;
      } else {
        return this.sellingUnit + 's';
      }
    }
  }

  /**
   * Validate and format input value
   * @param {Event} event
   * @param {boolean} allowDecimals - Allow decimal numbers
   */
  #validateAndFormatInput(event, allowDecimals = true) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    let value = input.value;
    
    value = value.replace(/,/g, '.');
    
    if (allowDecimals) {
      const cleaned = value.replace(/[^0-9.]/g, '');
      
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      } else if (parts.length === 2 && parts[1] && parts[1].length > 4) {
        value = parts[0] + '.' + parts[1].substring(0, 4);
      } else {
        value = cleaned;
      }
    } else {
      value = value.replace(/[^0-9]/g, '');
    }
    
    if (input.value !== value) {
      input.value = value;
    }
  }

  /**
   * Handle first product square feet input change.
   * @param {Event} event
   */
  handleFirstSqFtChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const sqFtValue = parseFloat(inputValue) || 0;
    
    if (sqFtValue === 0 || inputValue === '' || inputValue === '0') {
      this.#originalSqFt.first = 0;
      const firstUnitsInput = /** @type {HTMLInputElement} */ (this.refs.firstUnitsInput);
      const firstSqFtInput = /** @type {HTMLInputElement} */ (this.refs.firstSqFtInput);
      this.#isUpdating = true;
      firstUnitsInput.value = '';
      firstSqFtInput.value = '';
      this.#isUpdating = false;
      this.updateDisplays();
      return;
    }
    
    const originalSqFt = this.overage > 0 
      ? sqFtValue / (1 + this.overage / 100)
      : sqFtValue;
    
    this.#originalSqFt.first = originalSqFt;
    const sqFtWithOverage = sqFtValue;
    
    const unitsNeeded = this.firstCovers > 0 
      ? Math.ceil(sqFtWithOverage / this.firstCovers)
      : 0;
    
    this.#isUpdating = true;
    const firstUnitsInput = /** @type {HTMLInputElement} */ (this.refs.firstUnitsInput);
    const newUnitsValue = String(unitsNeeded);
    firstUnitsInput.value = newUnitsValue;
    firstUnitsInput.setAttribute('value', newUnitsValue);
    this.#updateUnitLabel('first', unitsNeeded);
    this.#isUpdating = false;
    
    this.updateDisplays();
  }

  /**
   * Handle first product units input change.
   * @param {Event} event
   */
  handleFirstUnitsChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const unitsValue = parseFloat(inputValue) || 0;
    
    if (unitsValue === 0 || inputValue === '' || inputValue === '0') {
      this.#originalSqFt.first = 0;
      const firstSqFtInput = /** @type {HTMLInputElement} */ (this.refs.firstSqFtInput);
      const firstUnitsInput = /** @type {HTMLInputElement} */ (this.refs.firstUnitsInput);
      this.#isUpdating = true;
      firstSqFtInput.value = '';
      firstUnitsInput.value = '';
      this.#isUpdating = false;
      this.updateDisplays();
      return;
    }
    
    const totalCoverage = unitsValue * this.firstCovers;
    const originalSqFt = this.overage > 0 
      ? totalCoverage / (1 + this.overage / 100)
      : totalCoverage;
    
    const sqFtWithOverage = totalCoverage;
    this.#originalSqFt.first = originalSqFt;
    
    this.#isUpdating = true;
    const firstSqFtInput = /** @type {HTMLInputElement} */ (this.refs.firstSqFtInput);
    const newValue = String(Math.round(sqFtWithOverage * 100) / 100);
    firstSqFtInput.value = newValue;
    firstSqFtInput.setAttribute('value', newValue);
    this.#updateUnitLabel('first', unitsValue);
    this.#isUpdating = false;
    
    this.updateDisplays();
  }

  /**
   * Handle second product square feet input change.
   * @param {Event} event
   */
  handleSecondSqFtChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const sqFtValue = parseFloat(inputValue) || 0;
    
    if (sqFtValue === 0 || inputValue === '' || inputValue === '0') {
      this.#originalSqFt.second = 0;
      const secondUnitsInput = /** @type {HTMLInputElement} */ (this.refs.secondUnitsInput);
      const secondSqFtInput = /** @type {HTMLInputElement} */ (this.refs.secondSqFtInput);
      this.#isUpdating = true;
      secondUnitsInput.value = '';
      secondSqFtInput.value = '';
      this.#isUpdating = false;
      this.updateDisplays();
      return;
    }
    
    const originalSqFt = this.overage > 0 
      ? sqFtValue / (1 + this.overage / 100)
      : sqFtValue;
    
    this.#originalSqFt.second = originalSqFt;
    const sqFtWithOverage = sqFtValue;
    
    const unitsNeeded = this.secondCovers > 0 
      ? Math.ceil(sqFtWithOverage / this.secondCovers)
      : 0;
    
    this.#isUpdating = true;
    const secondUnitsInput = /** @type {HTMLInputElement} */ (this.refs.secondUnitsInput);
    const newUnitsValue = String(unitsNeeded);
    secondUnitsInput.value = newUnitsValue;
    secondUnitsInput.setAttribute('value', newUnitsValue);
    this.#updateUnitLabel('second', unitsNeeded);
    this.#isUpdating = false;
    
    this.updateDisplays();
  }

  /**
   * Handle second product units input change.
   * @param {Event} event
   */
  handleSecondUnitsChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const unitsValue = parseFloat(inputValue) || 0;
    
    if (unitsValue === 0 || inputValue === '' || inputValue === '0') {
      this.#originalSqFt.second = 0;
      const secondSqFtInput = /** @type {HTMLInputElement} */ (this.refs.secondSqFtInput);
      const secondUnitsInput = /** @type {HTMLInputElement} */ (this.refs.secondUnitsInput);
      this.#isUpdating = true;
      secondSqFtInput.value = '';
      secondUnitsInput.value = '';
      this.#isUpdating = false;
      this.updateDisplays();
      return;
    }
    
    const totalCoverage = unitsValue * this.secondCovers;
    const originalSqFt = this.overage > 0 
      ? totalCoverage / (1 + this.overage / 100)
      : totalCoverage;
    
    const sqFtWithOverage = totalCoverage;
    this.#originalSqFt.second = originalSqFt;
    
    this.#isUpdating = true;
    const secondSqFtInput = /** @type {HTMLInputElement} */ (this.refs.secondSqFtInput);
    const newValue = String(Math.round(sqFtWithOverage * 100) / 100);
    secondSqFtInput.value = newValue;
    secondSqFtInput.setAttribute('value', newValue);
    this.#updateUnitLabel('second', unitsValue);
    this.#isUpdating = false;
    
    this.updateDisplays();
  }

  /**
   * Update add-to-cart button price based on total quantity
   * @param {number} totalUnits
   */
  #updateAddToCartPrice(totalUnits) {
    updateAddToCartPriceLabels(totalUnits);
  }

  /**
   * Update the display elements.
   */
  updateDisplays() {
    const firstUnits = parseFloat(this.refs.firstUnitsInput?.value || '0') || 0;
    const secondUnits = parseFloat(this.refs.secondUnitsInput?.value || '0') || 0;
    
    const firstCoverage = firstUnits * this.firstCovers;
    const secondCoverage = secondUnits * this.secondCovers;
    const totalCoverage = firstCoverage + secondCoverage;
    
    const firstUnitText = this.#getUnitText(firstUnits);
    const firstCoversText = this.firstCovers > 0 ? ` (${this.firstCovers} sq.ft each)` : '';
    if (this.refs.firstBoxesDisplay) {
      this.refs.firstBoxesDisplay.textContent = `${firstUnits} ${firstUnitText}${firstCoversText}`;
    }
    
    const secondUnitText = this.#getUnitText(secondUnits);
    const secondCoversText = this.secondCovers > 0 ? ` (${this.secondCovers} sq.ft each)` : '';
    if (this.refs.secondBoxesDisplay) {
      this.refs.secondBoxesDisplay.textContent = `${secondUnits} ${secondUnitText}${secondCoversText}`;
    }
    
    if (this.refs.completeCoverageDisplay) {
      this.refs.completeCoverageDisplay.textContent = `${Math.round(totalCoverage * 100) / 100} sq.ft`;
    }
    
    if (this.refs.firstCoverageDisplay) {
      this.refs.firstCoverageDisplay.textContent = `${Math.round(firstCoverage * 100) / 100} sq.ft`;
    }
    
    if (this.refs.secondCoverageDisplay) {
      this.refs.secondCoverageDisplay.textContent = `${Math.round(secondCoverage * 100) / 100} sq.ft`;
    }
    
    if (this.refs.overageDisplay) {
      if (this.overage > 0 && (this.#originalSqFt.first > 0 || this.#originalSqFt.second > 0)) {
        const totalOriginal = this.#originalSqFt.first + this.#originalSqFt.second;
        const totalWithOverage = totalOriginal * (1 + this.overage / 100);
        const overageSqFt = totalWithOverage - totalOriginal;
        this.refs.overageDisplay.textContent = `${this.overage}% (+${Math.round(overageSqFt * 100) / 100} sq.ft)`;
      } else {
        this.refs.overageDisplay.textContent = 'No overage';
      }
    }
    
    if (this.refs.firstQuantityInput) {
      this.refs.firstQuantityInput.value = String(firstUnits);
    }
    if (this.refs.secondQuantityInput) {
      this.refs.secondQuantityInput.value = String(secondUnits);
    }
    
    const totalUnits = firstUnits + secondUnits;
    this.#updateAddToCartPrice(totalUnits);
  }

  /**
   * Clear all calculations and reset displays.
   */
  clearCalculations() {
    const unitText = this.#getUnitText(0);
    const firstCoversText = this.firstCovers > 0 ? ` (${this.firstCovers} sq.ft each)` : '';
    const secondCoversText = this.secondCovers > 0 ? ` (${this.secondCovers} sq.ft each)` : '';
    
    if (this.refs.firstBoxesDisplay) {
      this.refs.firstBoxesDisplay.textContent = `0 ${unitText}${firstCoversText}`;
    }
    
    if (this.refs.secondBoxesDisplay) {
      this.refs.secondBoxesDisplay.textContent = `0 ${unitText}${secondCoversText}`;
    }
    
    if (this.refs.completeCoverageDisplay) {
      this.refs.completeCoverageDisplay.textContent = '0 sq.ft';
    }
    
    if (this.refs.firstCoverageDisplay) {
      this.refs.firstCoverageDisplay.textContent = '0 sq.ft';
    }
    
    if (this.refs.secondCoverageDisplay) {
      this.refs.secondCoverageDisplay.textContent = '0 sq.ft';
    }
    
    this.#updateAddToCartPrice(0);
  }

  /**
   * Set overage percentage and recalculate.
   * @param {number} percentage
   */
  setOverage(percentage) {
    this.overage = parseFloat(String(percentage)) || 0;
    
    const firstSqFtInput = /** @type {HTMLInputElement} */ (this.refs.firstSqFtInput);
    const secondSqFtInput = /** @type {HTMLInputElement} */ (this.refs.secondSqFtInput);
    
    if (this.#originalSqFt.first > 0) {
      const sqFtWithOverage = this.#originalSqFt.first * (1 + this.overage / 100);
      const unitsNeeded = this.firstCovers > 0 
        ? Math.ceil(sqFtWithOverage / this.firstCovers)
        : 0;
      
      this.#isUpdating = true;
      const newSqFtValue = String(Math.round(sqFtWithOverage * 100) / 100);
      firstSqFtInput.value = newSqFtValue;
      firstSqFtInput.setAttribute('value', newSqFtValue);
      
      const newUnitsValue = String(unitsNeeded);
      this.refs.firstUnitsInput.value = newUnitsValue;
      this.refs.firstUnitsInput.setAttribute('value', newUnitsValue);
      this.#updateUnitLabel('first', unitsNeeded);
      this.#isUpdating = false;
    }
    
    if (this.#originalSqFt.second > 0) {
      const sqFtWithOverage = this.#originalSqFt.second * (1 + this.overage / 100);
      const unitsNeeded = this.secondCovers > 0 
        ? Math.ceil(sqFtWithOverage / this.secondCovers)
        : 0;
      
      this.#isUpdating = true;
      const newSqFtValue = String(Math.round(sqFtWithOverage * 100) / 100);
      secondSqFtInput.value = newSqFtValue;
      secondSqFtInput.setAttribute('value', newSqFtValue);
      
      const newUnitsValue = String(unitsNeeded);
      this.refs.secondUnitsInput.value = newUnitsValue;
      this.refs.secondUnitsInput.setAttribute('value', newUnitsValue);
      this.#updateUnitLabel('second', unitsNeeded);
      this.#isUpdating = false;
    }
    
    this.updateDisplays();
  }
}

if (!customElements.get('quantity-calculator-star-cross')) {
  customElements.define('quantity-calculator-star-cross', QuantityCalculatorStarCross);
}
